import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectIsOpened, toggleWidget } from 'store/widget'
import { messagesActions, messagesSelectors } from 'store/messages'
import { settingsSelectors } from 'store/settings'
import { chatIsToRead } from 'utils/chats'
import { store } from 'store/index'
import { useConfigureChat } from './WidgetLayout'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'

export const WidgetLauncher = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isOpened = useSelector(selectIsOpened)
  const chats = useSelector(messagesSelectors.chats)
  const currentUser = useSelector(settingsSelectors.userDetails)
  const initializeChat = useConfigureChat()

  // True when at least one chat has an unread message (e.g. an operator replied to a ticket).
  const hasUnread = useMemo(
    () => Object.values(chats).some((chat) => chatIsToRead(chat, currentUser)),
    [chats, currentUser],
  )

  // Timestamp of the most recent unread message. Changes every time a new unread message arrives,
  // which lets us replay the bounce animation even when the dot is already showing.
  const unreadSignature = useMemo(
    () =>
      Object.values(chats)
        .filter((chat) => chatIsToRead(chat, currentUser))
        .reduce((max, chat) => Math.max(max, Number(chat.lastMessage?.timestamp ?? 0)), 0),
    [chats, currentUser],
  )

  // Briefly bounce the launcher whenever a new unread message comes in while the widget is closed.
  // The bounce lives on an inner element so it never clashes with the outer entrance animation.
  const [bounce, setBounce] = useState(false)
  useEffect(() => {
    if (!hasUnread || isOpened) return
    setBounce(true)
    const timeout = setTimeout(() => setBounce(false), 900)
    return () => clearTimeout(timeout)
  }, [unreadSignature, hasUnread, isOpened])

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    dispatch(toggleWidget())

    setTimeout(() => {
      const newIsOpened = store.getState().widgetState.isOpened
      if (newIsOpened) {
        analytics.track(ANALYTICS_EVENTS.WIDGET_OPEN)
        initializeChat().catch(console.error)
      } else {
        // Closing the widget: drop the open chat so reopening lands back on the chat list, not a stale detail.
        dispatch(messagesActions.setChatId(undefined))
      }
    }, 10)
  }

  return (
    // Outer element: fixed positioning + one-time entrance animation (kept isolated from the bounce).
    <div className="animate-bounce-in fixed right-5 bottom-5">
      <button
        type="button"
        aria-label={isOpened ? t('close') : t('needHelp')}
        aria-expanded={isOpened}
        className={`relative inline-flex cursor-pointer items-center justify-center rounded-full border-0 bg-primary p-4 text-primary-foreground shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid ${
          bounce ? 'animate-widget-bounce' : ''
        }`}
        onClick={onClick}
      >
        <div
          className={`icon-rotate-transition flex h-6 w-6 items-center justify-center ${isOpened ? 'icon-rotate-90' : ''}`}
        >
          <OdinIcon icon={isOpened ? ['far', 'xmark'] : ['far', 'comment']} className="text-2xl" />
        </div>
        {hasUnread && !isOpened && (
          <span className="absolute top-1 right-1 h-3 w-3 rounded-full bg-green-500 ring-2 ring-white" />
        )}
      </button>
    </div>
  )
}
